<script lang="ts">
  import { browser } from '$app/environment';
  import { invalidateAll } from '$app/navigation';
  import { page } from '$app/stores';
  import LL, { setLocale, locale } from '$i18n/i18n-svelte';
  import type { Locales } from '$i18n/i18n-types';
  import { locales } from '$i18n/i18n-util';
  import { loadLocaleAsync, loadNamespaceAsync } from '$i18n/i18n-util.async';
  import { replaceLocaleInUrl } from '$lib/path';
  import { createDropdownMenu, melt } from '@melt-ui/svelte';
  import { Globe } from 'lucide-svelte';

  const switchLocale = async (newLocale: Locales, updateHistoryState = true) => {
    if (!newLocale || $locale === newLocale) return;

    // load new dictionary from server
    await loadLocaleAsync(newLocale);
    await loadNamespaceAsync(newLocale, 'er');

    // select locale
    setLocale(newLocale);

    if (updateHistoryState) {
      // update url to reflect locale changes
      history.pushState({ locale: newLocale }, '', replaceLocaleInUrl($page.url, newLocale));
    }

    // run the `load` function again
    invalidateAll();
  };

  // update `lang` attribute
  $: if (browser) {
    document.querySelector('html')!.setAttribute('lang', $locale);
  }

  // update locale when navigating via browser back/forward buttons
  const handlePopStateEvent = async ({ state }: PopStateEvent) => switchLocale(state.locale, false);

  // update locale when page store changes
  $: if (browser) {
    const lang = $page.params.lang as Locales;
    switchLocale(lang, false);
    history.replaceState(
      { ...history.state, locale: lang },
      '',
      replaceLocaleInUrl($page.url, lang),
    );
  }

  const {
    elements: { menu, item, trigger },
  } = createDropdownMenu();
</script>

<svelte:window on:popstate={handlePopStateEvent} />

<div class="locale-switcher">
  <button use:melt={$trigger}>
    <span><Globe size="16" /></span>
  </button>
  <menu use:melt={$menu}>
    {#each locales as l}
      <li use:melt={$item}>
        <a class:active={l === $locale} href={replaceLocaleInUrl($page.url, l)}>
          {$LL.languages[l]()}
        </a>
      </li>
    {/each}
  </menu>
</div>

<style lang="postcss">
  menu {
    display: flex;
    flex-direction: column;
    padding: var(--space-3);
    font-size: var(--size-6);
    background-color: var(--background);
    border-radius: var(--radius-3);
  }

  li a {
    display: block;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-2);
    text-decoration: none;

    &:hover {
      background-color: var(--mauve-a2);
    }
  }

  button {
    background-color: transparent;
    padding: var(--space-3);
    width: 3.4rem;
  }
</style>
